import ApplicationLayoutModel from '../../../../../shared/models/applications/api/ApplicationLayoutModel';
import IResult from '../../../../../shared/models/result/IResult';

export const normalizeApplicationLayout = (
    source: ApplicationLayoutModel | null,
    appIds: string[]
): ApplicationLayoutModel => {
    const seen = new Set<string>();
    const unique = (ids: string[]) => ids.filter(id => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
    const layout = source || { revision: 0, canEdit: false, groups: [], ungroupedAppIds: [] };
    const groups = layout.groups.map(group => ({ ...group, appIds: unique(group.appIds) }));
    const ungroupedAppIds = unique(layout.ungroupedAppIds);
    appIds.forEach(id => {
        if (!seen.has(id)) {
            seen.add(id);
            ungroupedAppIds.push(id);
        }
    });
    return { ...layout, groups, ungroupedAppIds };
};

export const getGroupAppIds = (layout: ApplicationLayoutModel, selectedGroupId: string): Set<string> => {
    if (selectedGroupId === 'ungrouped') return new Set(layout.ungroupedAppIds);
    if (selectedGroupId !== 'all') return new Set(layout.groups.find(group => group.id === selectedGroupId)?.appIds || []);
    return new Set([...layout.groups.reduce((ids, group) => ids.concat(group.appIds), [] as string[]), ...layout.ungroupedAppIds]);
};

export const getSelectedAppIds = (groupAppIds: Set<string>, excludedAppIds: Set<string>): Set<string> =>
    new Set(Array.from(groupAppIds).filter(id => !excludedAppIds.has(id)));

export const setCategoryAppSelection = (excludedAppIds: Set<string>, appIds: string[], selected: boolean): Set<string> => {
    const next = new Set(excludedAppIds);
    appIds.forEach(id => { if (selected) next.delete(id); else next.add(id); });
    return next;
};

export const getCategorySearchAppIds = (layout: ApplicationLayoutModel, searchQuery: string): Set<string> => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return new Set();
    const ids = layout.groups.filter(group => group.name.toLowerCase().includes(query))
        .reduce((appIds, group) => appIds.concat(group.appIds), [] as string[]);
    if ('other'.includes(query)) ids.push(...layout.ungroupedAppIds);
    return new Set(ids);
};

export const getVisibleGroupAppIds = (layout: ApplicationLayoutModel, selectedGroupId: string, loadedAppIds: Set<string>): Set<string> =>
    new Set(Array.from(getGroupAppIds(layout, selectedGroupId)).filter(id => loadedAppIds.has(id)));

export const shouldReloadApplicationLayout = (result: Pick<IResult<ApplicationLayoutModel>, 'httpStatus' | 'errors'>): boolean =>
    result.httpStatus === 409 || (result.httpStatus === 400 && result.errors?.some(error =>
        error.includes('Layout must contain every eligible assistant')));
